import { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { ChevronLeft, Check, Footprints, Dumbbell, Utensils, Video, ClipboardCheck, ChevronRight } from "lucide-react";

const PHASE_BG = { RESET: "bg-primary text-primary-foreground", ESTABLISH: "bg-blue-600 text-white", PROGRESS: "bg-emerald-600 text-white", "OWN IT": "bg-[#7C3AED] text-white" };

export default function Lesson() {
  const { n } = useParams();
  const nav = useNavigate();
  const [L, setL] = useState(null);
  const [showTranscript, setShowTranscript] = useState(false);

  const load = async () => {
    try { const r = await api.get(`/lessons/${n}`); setL(r.data); setShowTranscript(!r.data.video_url); }
    catch (e) { toast.error(e.response?.status === 423 ? "This lesson is not unlocked yet." : formatApiError(e.response?.data?.detail)); nav("/app/learn", { replace: true }); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [n]);

  const complete = async () => {
    try { await api.post(`/lessons/${n}/complete`); toast.success("Lesson marked complete."); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  if (!L) return <div className="text-muted-foreground text-sm">Loading lesson…</div>;
  const week = Number(n);
  const targets = [
    { icon: Footprints, label: "WALK", value: `${L.targets.walk.toLocaleString()} steps/day starting target`, to: "/app/move" },
    { icon: Dumbbell, label: "TRAIN", value: L.targets.train, to: `/app/week/${week}#workouts` },
    { icon: Utensils, label: "EAT", value: L.targets.eat, to: "/app/eat" },
  ];

  return (
    <div className="space-y-6 max-w-3xl" data-testid={`lesson-${n}`}>
      <Link to="/app/learn" className="text-xs uppercase tracking-widest text-muted-foreground inline-flex items-center gap-1 hover:text-primary"><ChevronLeft size={14} /> Learn</Link>

      <div className={`${PHASE_BG[L.phase_key]} rounded-lg p-7 md:p-9`} data-testid="lesson-hero">
        <div className="text-[10px] uppercase tracking-[0.22em] opacity-80">Week {week} of 12 · {L.phase_label} · {L.duration || "~3 min"}</div>
        <h1 className="wordmark mt-3 text-3xl md:text-5xl uppercase leading-[0.95]">{L.title}</h1>
        {L.completed && <div className="mt-4 inline-flex items-center gap-2 bg-background/20 rounded px-3 py-1.5 text-xs uppercase tracking-widest"><Check size={14} strokeWidth={3} /> Completed</div>}
      </div>

      {L.video_url ? (
        <video controls playsInline preload="metadata" src={L.video_url.startsWith("/api") ? `${window.location.origin}${L.video_url}` : L.video_url} className="w-full rounded-lg bg-black aspect-video" data-testid="lesson-video" />
      ) : (
        <div className="rounded-lg border border-dashed border-border bg-card p-5 flex items-center gap-4" data-testid="lesson-video-placeholder">
          <div className="w-10 h-10 rounded-md bg-muted flex items-center justify-center shrink-0"><Video size={18} className="text-muted-foreground" /></div>
          <div className="text-sm text-muted-foreground">Founder video for this week is on its way. Read the lesson below — nothing here is blocked.</div>
        </div>
      )}

      <div className="bg-card border border-border rounded-lg p-6" data-testid="lesson-transcript-card">
        <button onClick={() => setShowTranscript(!showTranscript)} className="w-full flex items-center justify-between text-left" data-testid="lesson-transcript-toggle">
          <span className="label-eyebrow">Lesson transcript</span>
          <ChevronRight size={16} className={`transition-transform ${showTranscript ? "rotate-90" : ""}`} />
        </button>
        {showTranscript && <p className="mt-4 text-sm leading-relaxed text-foreground/85 whitespace-pre-line" data-testid="lesson-transcript">{L.script}</p>}
      </div>

      <div className="grid md:grid-cols-2 gap-4">
        <div className="bg-card border border-border rounded-lg p-6" data-testid="lesson-means">
          <div className="label-eyebrow">What it means</div>
          <p className="mt-3 text-sm leading-relaxed">{L.means}</p>
        </div>
        <div className="bg-card border border-border rounded-lg p-6" data-testid="lesson-why-later">
          <div className="label-eyebrow">Why it matters later</div>
          <p className="mt-3 text-sm leading-relaxed">{L.why_later}</p>
        </div>
      </div>

      <div className="bg-primary/5 border border-primary/40 rounded-lg p-6 flex items-start gap-4" data-testid="lesson-action">
        <div className="w-10 h-10 rounded-md bg-primary text-primary-foreground inline-flex items-center justify-center shrink-0"><ClipboardCheck size={20} /></div>
        <div className="flex-1">
          <div className="label-eyebrow text-primary">Your action this week · {L.action_title}</div>
          <p className="mt-2 text-sm leading-relaxed">{L.action_text}</p>
          {week === 6 || week === 12 ? (
            <Link to={`/app/review/${week === 6 ? "week6" : "week12"}`} className="mt-3 inline-flex items-center gap-1 text-xs uppercase tracking-widest text-primary hover:underline" data-testid="lesson-review-link">
              Open your Week {week} Foundation Review <ChevronRight size={12} />
            </Link>
          ) : null}
        </div>
      </div>

      <div className="rounded-lg bg-foreground text-background p-6 text-center font-head uppercase text-lg md:text-xl" data-testid="lesson-closing">{L.closing}</div>

      <div>
        <div className="label-eyebrow mb-3">This week's targets</div>
        <div className="grid sm:grid-cols-3 gap-3">
          {targets.map((t) => (
            <Link key={t.label} to={t.to} className="bg-card border border-border rounded-lg p-4 lift block" data-testid={`lesson-target-${t.label.toLowerCase()}`}>
              <t.icon size={18} className="text-primary" />
              <div className="font-head uppercase mt-2">{t.label}</div>
              <div className="text-xs text-muted-foreground mt-1 leading-snug">{t.value}</div>
            </Link>
          ))}
        </div>
      </div>

      {!L.completed && (
        <button onClick={complete} className="btn-primary w-full min-h-[44px]" data-testid="lesson-complete-button">Mark lesson complete</button>
      )}
    </div>
  );
}
